export type SocialPostEditFields = {
  message: string;
  links: string[];
  tags: string[];
};

export function splitSocialPostEditFields(fields: SocialPostEditFields): SocialPostEditFields {
  const links = fields.links.map(String).filter(Boolean);
  const tags = fields.tags.map(String).filter(Boolean);
  const linkSet = new Set(links.map(normalizeLink).filter(Boolean));
  const tagSet = new Set(tags.map(normalizeTag).filter(Boolean));
  const lines = fields.message.replace(/\r\n/g, '\n').split('\n');
  const kept: string[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const rawLine = lines[i] || '';
    const line = rawLine.trim();

    if (isGeneratedReadPromptLine(line, linkSet)) {
      continue;
    }

    if (isKnownLinkOnlyLine(line, linkSet)) {
      continue;
    }

    if (isKnownHashtagOnlyLine(line, tagSet)) {
      continue;
    }

    kept.push(rawLine.trimEnd());
  }

  return {
    message: normalizeMessage(kept),
    links,
    tags,
  };
}

function normalizeMessage(lines: string[]): string {
  return lines.join('\n').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function normalizeLink(link: string): string {
  return link.trim().replace(/[),.;:]+$/g, '');
}

function normalizeTag(tag: string): string {
  return tag.trim().replace(/^#+/, '').toLowerCase();
}

function extractLinks(text: string): string[] {
  const matches = text.match(/https?:\/\/[^\s<>"'[\]()]+/gi) || [];
  return matches.map(normalizeLink).filter(Boolean);
}

function isGeneratedReadPromptLine(line: string, linkSet: Set<string>): boolean {
  const match = line.match(/^read\s+our\s+(?:blog|article|thought\s+leadership)\s*-?\s*(.*)$/i);
  if (!match) return false;

  const remainder = (match[1] || '').trim();
  if (!remainder) return true;

  const links = extractLinks(remainder);
  if (links.length === 0) return false;
  if (links.some((link) => !linkSet.has(link))) return false;

  return stripLinks(remainder).replace(/[-\s]+/g, '') === '';
}

function isKnownLinkOnlyLine(line: string, linkSet: Set<string>): boolean {
  if (!line || linkSet.size === 0) return false;
  const links = extractLinks(line);
  if (links.length === 0) return false;
  if (links.some((link) => !linkSet.has(link))) return false;
  return stripLinks(line).replace(/[),.;:\s]+/g, '') === '';
}

function stripLinks(text: string): string {
  return text.replace(/https?:\/\/[^\s<>"'[\]()]+/gi, '');
}

function isKnownHashtagOnlyLine(line: string, tagSet: Set<string>): boolean {
  if (!line || tagSet.size === 0) return false;
  const tokens = line.split(/[\s,]+/).filter(Boolean);
  if (tokens.length === 0) return false;
  return tokens.every((token) => token.startsWith('#') && tagSet.has(normalizeTag(token)));
}
